"use client";

import { motion, useReducedMotion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import SectionHeader from "@/components/ui/section-header";
import { GREEN } from "@/lib/constants";

const STEPS = [
  {
    title: "Your app sends a request",
    body: "Your scraper, browser, or automation tool sends its request to your Proxidize access point instead of going to the target directly.",
  },
  {
    title: "Proxidize selects a residential IP",
    body: "Proxidize picks an eligible residential IP that matches your targeting (country, city, or ISP) and session setting (rotating or sticky).",
  },
  {
    title: "The target sees the residential IP",
    body: "The destination website receives the request from that residential IP, not your server's IP, and its response returns the same way.",
  },
];

export default function ResidentialHowItWorks() {
  const prefersReducedMotion = useReducedMotion();

  return (
    <section
      className="py-section-sm border-border border-b"
      aria-labelledby="residential-how-it-works-heading"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <SectionHeader
            eyebrow="How It Works"
            id="residential-how-it-works-heading"
            heading="How residential proxies work"
            body="A residential proxy routes your request through an IP address assigned by an ISP to a real residential connection. The destination website sees that residential IP instead of your server's IP."
          />
          <p className="text-muted-foreground mt-2 mb-8 text-base leading-relaxed">
            Proxidize connects your scraper, browser, or automation tool to a residential access
            point. Select a country, city, or ISP, and choose whether the IP rotates between
            requests or stays active for a longer session.
          </p>
        </div>

        <ol className="grid list-none grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-3">
          {STEPS.map((step, i) => (
            <motion.li
              key={step.title}
              initial={prefersReducedMotion ? false : { opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={scrollRevealViewport}
              transition={{ duration: 0.45, delay: i * 0.06 }}
              className="flex flex-col gap-3"
            >
              <span
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold tabular-nums"
                style={{
                  background: `${GREEN}15`,
                  color: "var(--brand-green)",
                  border: `1px solid ${GREEN}33`,
                }}
              >
                {i + 1}
              </span>
              <div>
                <p className="text-foreground mb-1 text-sm font-semibold">{step.title}</p>
                <p className="text-muted-foreground text-sm leading-relaxed">{step.body}</p>
              </div>
            </motion.li>
          ))}
        </ol>
      </div>
    </section>
  );
}
